import React, { useState } from 'react';
import { X, ExternalLink, Calendar, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const NewsletterPopup = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fade-in">
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-gradient-to-br from-[#1a0f0f]/90 via-brand-brown/85 to-black/90 rounded-3xl shadow-2xl border-2 border-[#c5b896]/30 overflow-hidden backdrop-blur-xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 bg-brand-maroon/80 hover:bg-brand-maroon text-white rounded-full transition-all duration-300 hover:scale-110 backdrop-blur-sm"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Scrollable content */}
        <div className="overflow-y-auto max-h-[85vh] p-6 md:p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-block px-3 py-1.5 bg-[#c5b896]/20 rounded-full border border-[#c5b896]/40 mb-3 backdrop-blur-sm">
              <span className="text-[#c5b896] font-bold text-xs">🗞 OFFICIAL AGENCY NEWSLETTER — ISSUE NO. 3</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#c5b896] mb-3 drop-shadow-[0_0_15px_rgba(197,184,150,0.3)]">
              We Just Launched Something Big
            </h2>
            <p className="text-lg text-white/90 mb-2 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
              Chef Compliance UK Has Landed
            </p>
            <div className="flex items-center justify-center gap-2 text-[#c5b896]/60 text-xs">
              <Calendar className="w-3 h-3" />
              <span>June 2026 · Swinton, Manchester, UK</span>
            </div>
          </div>

          {/* Main content */}
          <div className="space-y-6">
            {/* Launch announcement */}
            <div className="bg-black/30 backdrop-blur-md rounded-2xl p-6 border border-[#c5b896]/20">
              <h3 className="text-xl font-bold text-[#c5b896] mb-3 drop-shadow-[0_2px_8px_rgba(197,184,150,0.6)]">
                Chef Compliance UK Has Landed
              </h3>
              <p className="text-white/90 leading-relaxed mb-3 text-sm drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
                We're absolutely buzzing to announce that <strong>Chef Compliance UK</strong> is officially live and fully operational online — and we built every pixel of it.
              </p>
              <p className="text-white/90 leading-relaxed text-sm drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
                From brand design to content architecture, from booking systems to pricing clarity — JRT Chideme Media handled the entire digital transformation.
              </p>
              
              {/* Visit button */}
              <a
                href="https://chefcomplianceuk.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-brand-maroon/90 hover:bg-brand-maroon text-white font-semibold rounded-lg transition-all duration-300 hover:scale-105 shadow-lg shadow-brand-maroon/30 text-sm backdrop-blur-sm"
              >
                Visit Chef Compliance UK
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* What we built - Condensed */}
            <div>
              <h3 className="text-lg font-bold text-[#c5b896] mb-4 text-center drop-shadow-[0_0_12px_rgba(197,184,150,0.3)]">
                What We Built for Danny
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { title: "Brand Identity", icon: "🎨" },
                  { title: "Full Website", icon: "💻" },
                  { title: "Booking System", icon: "📋" },
                  { title: "Pricing Packages", icon: "💷" },
                  { title: "Documents Suite", icon: "📄" },
                  { title: "Social Content", icon: "📱" },
                  { title: "Hosting", icon: "🚀" },
                  { title: "Business Plan", icon: "📊" }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 bg-black/20 backdrop-blur-sm rounded-xl p-3 border border-[#c5b896]/20">
                    <span className="text-lg">{item.icon}</span>
                    <span className="text-xs text-white font-medium drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">{item.title}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 text-center">
              <div className="bg-gradient-to-br from-brand-maroon/20 to-transparent rounded-xl p-6 border border-brand-maroon/30">
                <div className="text-4xl font-bold text-brand-maroon drop-shadow-[0_2px_8px_rgba(139,35,50,0.8)]">8+</div>
                <div className="text-sm text-white/80 mt-2">Deliverables Completed</div>
              </div>
              <div className="bg-gradient-to-br from-brand-olive/20 to-transparent rounded-xl p-6 border border-brand-olive/30">
                <div className="text-4xl font-bold text-brand-olive drop-shadow-[0_2px_8px_rgba(165,166,117,0.8)]">1k+</div>
                <div className="text-sm text-white/80 mt-2">Lines of Custom Code</div>
              </div>
              <div className="bg-gradient-to-br from-brand-maroon/20 to-transparent rounded-xl p-6 border border-brand-maroon/30">
                <div className="text-4xl font-bold text-brand-maroon drop-shadow-[0_2px_8px_rgba(139,35,50,0.8)]">∞</div>
                <div className="text-sm text-white/80 mt-2">Value Delivered</div>
              </div>
            </div>

            {/* Pull quote */}
            <div className="text-center p-8 bg-gradient-to-r from-brand-olive/10 via-brand-maroon/10 to-brand-olive/10 rounded-2xl border-l-4 border-brand-maroon">
              <p className="text-xl italic text-white/90 leading-relaxed drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                "We don't just build websites. We build digital businesses that work while you sleep — and Chef Compliance UK is living proof."
              </p>
            </div>

            {/* Team Announcement - Elizabeth Gadd CFO */}
            <div className="bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-2xl p-8 border-2 border-brand-maroon/40">
              <div className="inline-block px-4 py-2 bg-brand-maroon/20 rounded-full border border-brand-maroon/40 mb-6">
                <span className="text-brand-maroon font-bold text-sm">🌟 BIG TEAM NEWS</span>
              </div>
              
              <h3 className="text-3xl font-bold text-white mb-6 drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]">
                A New Force Joins the JRT Family
              </h3>

              <div className="flex flex-col md:flex-row gap-8 items-start">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 bg-gradient-to-br from-brand-maroon to-brand-olive rounded-full flex items-center justify-center border-4 border-brand-maroon/40 shadow-lg shadow-brand-maroon/50">
                    <span className="text-4xl font-bold text-white">EG</span>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-brand-maroon mb-1 drop-shadow-[0_2px_8px_rgba(139,35,50,0.8)]">
                    Elizabeth Gadd
                  </h4>
                  <p className="text-brand-olive font-semibold mb-4 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                    Chief Financial Officer · Second-in-Command
                  </p>

                  <p className="text-white/90 leading-relaxed mb-4 drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
                    We are <strong>immensely proud and genuinely excited</strong> to formally welcome <strong>Elizabeth Gadd</strong> as JRT Chideme Media's new Chief Financial Officer — and the agency's second-in-command.
                  </p>

                  <p className="text-white/90 leading-relaxed mb-4 drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
                    Elizabeth brings extraordinary strategic financial acumen, unshakeable attention to detail, and a business mindset that is already elevating everything we do. Since stepping into her role, <strong>she has already hit the ground running</strong> — driving internal financial structure, tightening operational efficiency, and positioning JRT Chideme Media for serious, sustainable growth.
                  </p>

                  {/* Quote */}
                  <div className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border-l-4 border-brand-olive mt-6">
                    <p className="text-white/90 italic leading-relaxed drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
                      "JRT Chideme Media is not just an agency — it is a movement. Our mission is to give small businesses the kind of digital infrastructure that only big brands used to afford. I am here to make sure that mission is built on rock-solid ground."
                    </p>
                    <p className="text-brand-maroon font-semibold mt-3 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                      — Elizabeth Gadd, CFO · JRT Chideme Media
                    </p>
                  </div>

                  <p className="text-white/80 mt-6 drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
                    Please join us in welcoming Elizabeth to the team — and watch this space. <strong className="text-brand-maroon">Great things are coming.</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* CTA - Simplified */}
            <div className="text-center bg-black/20 backdrop-blur-md rounded-2xl p-6 border border-[#c5b896]/30">
              <p className="text-xs text-[#c5b896] font-bold mb-2 uppercase tracking-wider">Ready to Grow?</p>
              <h4 className="text-xl font-bold text-white mb-3 drop-shadow-[0_0_12px_rgba(255,255,255,0.3)]">
                Your Brand Deserves to Be Seen
              </h4>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => window.location.href = 'mailto:JRTChidemeMedia86@gmail.com'}
                  className="bg-brand-maroon/90 hover:bg-brand-maroon text-white font-bold shadow-lg shadow-brand-maroon/30 text-sm backdrop-blur-sm"
                >
                  Start Your Project
                </Button>
                <Button
                  onClick={() => window.location.href = 'tel:0799917033'}
                  variant="outline"
                  className="border-2 border-[#c5b896] text-[#c5b896] hover:bg-[#c5b896]/20 font-bold text-sm backdrop-blur-sm"
                >
                  Call Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterPopup;
